import React, { useState, useEffect, useRef } from 'react';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import {
  FaMicrophone,
  FaMicrophoneSlash,
  FaHeadphones,
  FaPhone,
  FaCog,
  FaDesktop,
  FaExclamationTriangle,
  FaClosedCaptioning,
  FaLock,
} from 'react-icons/fa';
import api from '@/services/api';
import socketService from '@/services/socket';
import { usePermissions } from '@/hooks/usePermissions';
import { PermissionName } from '@/types/roles';
import soundService from '@/services/sounds';
import { useToast } from '@/components/common/ToastProvider';
import { useAudioDevices } from '@/hooks/useAudioDevices';
import { useVoicePreferences } from '@/hooks/useVoicePreferences';
import VoiceChannelJoinAnimation from './VoiceChannelJoinAnimation';
import ScreenShareAnimation from './ScreenShareAnimation';
import { useVoiceCall } from '@/contexts/VoiceCallContext';

interface VoiceChannelProps {
  channelId: string;
}

interface VoiceSessionResponse {
  user: {
    id: string;
    username: string;
  };
  isMuted: boolean;
  isDeafened: boolean;
}

export default function VoiceChannel({ channelId }: VoiceChannelProps) {
  const [isConnected, setIsConnected] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isDeafened, setIsDeafened] = useState(false);
  const [speaking, setSpeaking] = useState<Record<string, boolean>>({});
  const [showSettings, setShowSettings] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);
  const [screenResolution, setScreenResolution] = useState<'720p' | '1080p' | 'auto'>('auto');
  const [screenFramerate, setScreenFramerate] = useState<15 | 30 | 60>(30);
  const [screenSourceType, setScreenSourceType] = useState<'screen' | 'window'>('screen');
  const [isScreenMuted, setIsScreenMuted] = useState(false);
  const [connectionQuality, setConnectionQuality] = useState<'good' | 'medium' | 'poor'>('good');
  const [latency, setLatency] = useState<number>(0);
  const [packetLoss, setPacketLoss] = useState<number>(0);
  const [showScreenSettings, setShowScreenSettings] = useState(false);
  const [screenSharesVisible, setScreenSharesVisible] = useState<Record<string, boolean>>({}); // userId -> is visible
  const [showJoinAnimation, setShowJoinAnimation] = useState(false);
  const [showScreenShareAnimation, setShowScreenShareAnimation] = useState(false);
  const [screenShareAnimationType, setScreenShareAnimationType] = useState<'start' | 'stop'>(
    'start',
  );
  const [channelName, setChannelName] = useState<string>('');
  const [hasClicked, setHasClicked] = useState(false);
  // peers are managed in VoiceCallContext now
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const speakingRafRef = useRef<number | null>(null);
  const speakingFlagRef = useRef<boolean>(false);
  const qualityMonitorIntervalRef = useRef<number | null>(null);

  const currentUser = useCurrentUser();
  const { captionsEnabled, captions, toggleCaptions } = useVoiceCall();
  const toast = useToast();
  const { hasPermission } = usePermissions();
  const canKickFromVoice = hasPermission(PermissionName.KICK_FROM_VOICE);

  const kickFromVoice = async (userId: string) => {
    try {
      await api.post(`/voice/channels/${channelId}/kick`, { userId });
      toast.success('User removed from voice channel');
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Failed to kick user');
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input or textarea
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }

      if (e.key === 'c' || e.key === 'C') {
        toggleCaptions();
      }
    };

    if (isConnected) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isConnected, toggleCaptions]);
  const {
    localStream: ctxLocalStream,
    users: ctxUsers,
    joinVoiceChannel: ctxJoinVoiceChannel,
    leaveVoiceChannel: ctxLeaveVoiceChannel,
    addScreenTrack: ctxAddScreenTrack,
    removeScreenTrack: ctxRemoveScreenTrack,
    isInCall: ctxIsInCall,
    reconnectingUsers: ctxReconnectingUsers,
  } = useVoiceCall();

  // Voice settings hooks
  const {
    inputDevices,
    outputDevices,
    selectedInputId,
    selectedOutputId,
    setInputDevice,
    setOutputDevice,
    refreshDevices,
  } = useAudioDevices();

  const { preferences: voicePreferences, updatePreferences: updateVoicePreferences } =
    useVoicePreferences();

  const speakingUsers = React.useMemo(() => {
    return ctxUsers.filter((u) => speaking[u.id]);
  }, [ctxUsers, speaking]);

  const availableScreenShares = React.useMemo(() => {
    return ctxUsers.filter((user) => screenSharesVisible[user.id] === false);
  }, [ctxUsers, screenSharesVisible]);

  const speakingUsersElements = React.useMemo(() => {
    return speakingUsers.map((u) => (
      <span key={u.id} className="px-2 py-1 rounded-full bg-green-600/80">
        {u.username}
      </span>
    ));
  }, [speakingUsers]);

  const ctxUsersElements = React.useMemo(() => {
    return ctxUsers.map((user) => (
      <div
        key={user.id}
        className={`flex items-center gap-2 px-2 py-1 rounded ${
          isConnected && speaking[user.id]
            ? 'bg-green-100 dark:bg-green-900/40 ring-1 ring-green-500/60'
            : ''
        }`}
      >
        <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center">
          <span className="text-brand-on-primary text-sm font-bold">
            {user.username[0].toUpperCase()}
          </span>
        </div>
        <span className="flex-1">{user.username}</span>
        {ctxReconnectingUsers && ctxReconnectingUsers[user.id] && (
          <span className="text-xs italic text-yellow-500 dark:text-yellow-300 mr-2">
            Reconnecting
          </span>
        )}
        {isConnected && speaking[user.id] && (
          <span className="text-xs text-green-500 dark:text-green-300">Speaking</span>
        )}
        {user.isMuted && <FaMicrophoneSlash className="text-brand-error" size={14} />}
        {user.isDeafened && <FaHeadphones className="text-brand-outline" size={14} />}
        {canKickFromVoice && user.id !== currentUser?.id && (
          <button
            onClick={() => kickFromVoice(user.id)}
            className="ml-auto text-xs px-2 py-0.5 rounded bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
            title={`Remove ${user.username} from voice`}
          >
            Kick
          </button>
        )}
      </div>
    ));
  }, [ctxUsers, isConnected, speaking, ctxReconnectingUsers, canKickFromVoice, currentUser?.id]);

  const availableScreenSharesElements = React.useMemo(() => {
    return availableScreenShares.map((user) => (
      <button
        key={user.id}
        onClick={() => setScreenSharesVisible((prev) => ({ ...prev, [user.id]: true }))}
        className="w-full text-left px-3 py-2 rounded-lg bg-blue-100 dark:bg-blue-900/40 hover:bg-blue-200 dark:hover:bg-blue-900/60 transition-colors border border-blue-300 dark:border-blue-700 focus-visible:ring-2 focus-visible:ring-blue-500 focus:outline-none"
        aria-label={`View screen share from ${user.username}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FaDesktop className="text-brand-primary dark:text-brand-primary" size={16} />
            <span className="text-sm font-medium text-brand-primary dark:text-brand-primary">
              {user.username} is sharing screen
            </span>
          </div>
          <span className="text-xs text-brand-primary dark:text-brand-primary">Click to view</span>
        </div>
      </button>
    ));
  }, [availableScreenShares, setScreenSharesVisible]);

  const captionsElements = React.useMemo(() => {
    return captions.map((cap, i) => (
      <div
        key={`${cap.userId}-${cap.timestamp}-${i}`}
        className="bg-black/70 rounded-lg px-4 py-2 text-brand-on-primary max-w-2xl text-center backdrop-blur-sm"
      >
        <span className="font-bold text-sm text-brand-on-surface-variant mr-2">
          {cap.username}:
        </span>
        <span className="text-base font-medium">{cap.text}</span>
        {!cap.isFinal && <span className="opacity-50 inline-block ml-1 animate-pulse">...</span>}
      </div>
    ));
  }, [captions]);

  const inputDevicesElements = React.useMemo(() => {
    return inputDevices.map((device) => (
      <option key={device.deviceId} value={device.deviceId}>
        {device.label}
      </option>
    ));
  }, [inputDevices]);

  const outputDevicesElements = React.useMemo(() => {
    return outputDevices.map((device) => (
      <option key={device.deviceId} value={device.deviceId}>
        {device.label}
      </option>
    ));
  }, [outputDevices]);

  useEffect(() => {
    // Fetch initial info for UI (name, speaking map)
    fetchActiveSessions();
    // Mirror context in-call state
    setIsConnected(Boolean(ctxIsInCall));
  }, [channelId, ctxIsInCall]);

  const fetchActiveSessions = async () => {
    try {
      const response = await api.get(`/voice/channels/${channelId}/sessions`);
      const mapped = response.data.sessions.map((s: VoiceSessionResponse) => ({
        id: s.user.id,
        username: s.user.username,
        isMuted: s.isMuted,
        isDeafened: s.isDeafened,
      }));
      // primary membership updates come via context; ensure speaking map has keys
      setSpeaking((prev) => {
        const next = isConnected ? { ...prev } : {};
        mapped.forEach((u: { id: string }) => {
          if (next[u.id] === undefined) {
            next[u.id] = false;
          }
        });
        return next;
      });
    } catch (error) {
      console.error('Error fetching voice sessions:', error);
    }
  };

  // Mirror context localStream into local processing stream
  useEffect(() => {
    if (ctxLocalStream) {
      // start speaking detection when context provides a stream
      startLocalSpeakingDetection(ctxLocalStream);
    } else {
      // stop speaking detection if stream lost
      if (speakingRafRef.current) {
        cancelAnimationFrame(speakingRafRef.current);
        speakingRafRef.current = null;
      }
      speakingFlagRef.current = false;
      setSpeaking({});
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      analyserRef.current = null;
    }
  }, [ctxLocalStream]);

  const handleJoinAnimationComplete = React.useCallback(() => {
    setShowJoinAnimation(false);
  }, []);

  const handleScreenShareAnimationComplete = React.useCallback(() => {
    setShowScreenShareAnimation(false);
  }, []);

  const joinVoiceChannel = async () => {
    try {
      const channelResponse = await api.get(`/channels/${channelId}`);
      setChannelName(channelResponse.data.channel?.name || 'Voice Channel');
      setShowJoinAnimation(true);
      await ctxJoinVoiceChannel(channelId, channelResponse.data.channel?.name || 'Voice Channel');
      // fetch sessions for speaking map
      fetchActiveSessions();
    } catch (error) {
      console.error('Error joining voice channel:', error);
      setShowJoinAnimation(false);
      toast.error('Failed to join voice channel');
    }
  };

  const leaveVoiceChannel = async () => {
    try {
      setShowJoinAnimation(false);
      // Stop connection quality monitoring
      if (qualityMonitorIntervalRef.current) {
        clearInterval(qualityMonitorIntervalRef.current);
        qualityMonitorIntervalRef.current = null;
      }

      // Stop screen sharing if active
      if (isScreenSharing) {
        stopScreenShare();
      }

      if (speakingRafRef.current) {
        cancelAnimationFrame(speakingRafRef.current);
      }
      speakingFlagRef.current = false;
      setSpeaking({});
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      analyserRef.current = null;

      // Let context handle API leave, peers and streams
      await ctxLeaveVoiceChannel();
      setIsConnected(false);
      setConnectionQuality('good');
      setLatency(0);
      setPacketLoss(0);
    } catch (error) {
      console.error('Error leaving voice channel:', error);
      toast.error('Failed to leave voice channel');
    }
  };

  const toggleMute = async () => {
    if (ctxLocalStream) {
      const audioTrack = ctxLocalStream.getAudioTracks()[0];
      audioTrack.enabled = !audioTrack.enabled;
      setIsMuted(!audioTrack.enabled);

      // Play mute/unmute sound
      soundService.play(!audioTrack.enabled ? 'mute' : 'unmute');

      await api.patch('/voice/state', { isMuted: !audioTrack.enabled });
    }
  };

  const toggleDeafen = async () => {
    setIsDeafened(!isDeafened);
    await api.patch('/voice/state', { isDeafened: !isDeafened });
  };

  const getScreenConstraints = () => {
    const constraints: any = { audio: false, video: true };

    if (screenResolution !== 'auto') {
      const resolutions = {
        '720p': { width: 1280, height: 720 },
        '1080p': { width: 1920, height: 1080 },
      };
      constraints.video = {
        width: { ideal: resolutions[screenResolution].width },
        height: { ideal: resolutions[screenResolution].height },
        frameRate: { ideal: screenFramerate },
      };
    } else {
      constraints.video = { frameRate: { ideal: screenFramerate } };
    }

    return constraints;
  };

  const startScreenShare = async () => {
    try {
      const constraints = getScreenConstraints();
      const displayConstraints: DisplayMediaStreamOptions & { preferCurrentTab?: boolean } = {
        video: constraints.video as any,
        audio: false,
      };

      // If user selected window, ask them to choose
      if (screenSourceType === 'window') {
        displayConstraints.preferCurrentTab = false;
      }

      const stream = await navigator.mediaDevices.getDisplayMedia(displayConstraints);

      setScreenStream(stream);
      setIsScreenSharing(true);

      // Show screen share animation
      setScreenShareAnimationType('start');
      setShowScreenShareAnimation(true);

      // Delegate adding track to context-managed peers
      ctxAddScreenTrack?.(stream);

      // Handle user stopping screen share via browser button
      const videoTrack = stream.getVideoTracks()[0];
      videoTrack.onended = () => {
        stopScreenShare();
      };

      // Mute video track if user has mute enabled
      if (isScreenMuted) {
        videoTrack.enabled = false;
      }

      socketService.emit('voice:screen-share-started', {
        channelId,
        sourceType: screenSourceType,
        resolution: screenResolution,
        framerate: screenFramerate,
      });
    } catch (error) {
      console.error('Error starting screen share:', error);
      toast.error('Failed to start screen sharing. Please check your permissions.');
    }
  };

  const stopScreenShare = () => {
    if (screenStream) {
      screenStream.getTracks().forEach((track) => track.stop());

      // Remove screen tracks from peer connections (context-handled)
      ctxRemoveScreenTrack?.();
      setScreenStream(null);
      setIsScreenSharing(false);
      setIsScreenMuted(false);

      // Show screen share stop animation
      setScreenShareAnimationType('stop');
      setShowScreenShareAnimation(true);

      socketService.emit('voice:screen-share-stopped', { channelId });
    }
  };

  const toggleScreenMute = () => {
    if (screenStream) {
      const videoTrack = screenStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = isScreenMuted;
        setIsScreenMuted(!isScreenMuted);
        socketService.emit('voice:screen-muted', {
          channelId,
          muted: !isScreenMuted,
        });
      }
    }
  };

  const startLocalSpeakingDetection = (stream: MediaStream) => {
    if (!currentUser.id) return;
    try {
      const audioContext = audioContextRef.current ?? new AudioContext();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 1024;
      analyserRef.current = analyser;
      source.connect(analyser);
      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const tick = () => {
        analyser.getByteFrequencyData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          sum += dataArray[i];
        }
        const avg = sum / dataArray.length;
        const isSpeaking = avg > 12; // lightweight threshold
        if (isSpeaking !== speakingFlagRef.current) {
          speakingFlagRef.current = isSpeaking;
          setSpeaking((prev) => ({ ...prev, [currentUser.id ?? '']: isSpeaking }));
          socketService.emit('voice:speaking', { channelId, speaking: isSpeaking });
          try {
            // Notify local voice context of activity so we don't auto-disconnect when only the local user is speaking
            window.dispatchEvent(new CustomEvent('voice:local-activity'));
          } catch (e) {
            // ignore in environments without window
          }
        }
        speakingRafRef.current = requestAnimationFrame(tick);
      };

      speakingRafRef.current = requestAnimationFrame(tick);
    } catch (err) {
      console.error('Error starting speaking detection:', err);
    }
  };

  return (
    <>
      {/* Voice Channel Join Animation */}
      {showJoinAnimation && (
        <VoiceChannelJoinAnimation
          username={currentUser.username || 'User'}
          channelName={channelName}
          onComplete={handleJoinAnimationComplete}
        />
      )}

      {/* Screen Share Animation */}
      {showScreenShareAnimation && (
        <ScreenShareAnimation
          username={currentUser.username || 'User'}
          isStarting={screenShareAnimationType === 'start'}
          onComplete={handleScreenShareAnimationComplete}
        />
      )}

      <div className="flex-1 flex flex-col items-center justify-center p-8">
        <div className="max-w-md w-full space-y-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-2">Voice Channel</h2>
            <div className="flex flex-col items-center gap-1.5">
              <p className="text-brand-on-surface-variant dark:text-brand-on-surface-variant font-medium">
                {isConnected ? 'Connected' : 'Not connected'}
              </p>
              <div
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/15 dark:bg-emerald-950/40 text-emerald-400 border border-emerald-500/40 text-xs font-semibold shadow-sm"
                title="Voice streams are encrypted end-to-end via WebRTC DTLS-SRTP"
              >
                <FaLock size={10} className="text-emerald-400" />
                <span>End-to-End Encrypted (DTLS-SRTP)</span>
              </div>
            </div>
          </div>

          {/* Connection Quality Warning */}
          {isConnected && connectionQuality !== 'good' && (
            <div
              className={`rounded-lg p-4 border ${
                connectionQuality === 'poor'
                  ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700'
                  : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700'
              }`}
            >
              <div className="flex items-start gap-3">
                <FaExclamationTriangle
                  className={
                    connectionQuality === 'poor'
                      ? 'text-brand-error dark:text-brand-error'
                      : 'text-yellow-500 dark:text-yellow-400'
                  }
                  size={20}
                />
                <div className="flex-1">
                  <h4
                    className={`font-semibold text-sm mb-1 ${
                      connectionQuality === 'poor'
                        ? 'text-red-800 dark:text-red-300'
                        : 'text-yellow-800 dark:text-yellow-300'
                    }`}
                  >
                    {connectionQuality === 'poor'
                      ? 'Poor Connection Quality'
                      : 'Connection Quality Warning'}
                  </h4>
                  <p
                    className={`text-xs mb-2 ${
                      connectionQuality === 'poor'
                        ? 'text-red-700 dark:text-brand-error'
                        : 'text-yellow-700 dark:text-yellow-400'
                    }`}
                  >
                    Latency: {latency.toFixed(0)}ms | Packet Loss: {packetLoss.toFixed(1)}%
                  </p>
                  <ul
                    className={`text-xs space-y-1 ${
                      connectionQuality === 'poor'
                        ? 'text-brand-error dark:text-brand-error'
                        : 'text-yellow-500 dark:text-yellow-400'
                    }`}
                  >
                    {isScreenSharing && <li>• Consider stopping screen share</li>}
                    <li>• Check your network connection</li>
                    <li>• Move closer to your router</li>
                    <li>• Close bandwidth-heavy applications</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {isConnected && (
            <div className="bg-brand-background text-brand-on-primary rounded-lg p-3 text-center shadow">
              {speakingUsers.length > 0 ? (
                <div className="flex flex-wrap gap-2 justify-center items-center text-sm">
                  <span className="text-brand-on-surface-variant">Speaking:</span>
                  {speakingUsersElements}
                </div>
              ) : (
                <span className="text-brand-on-surface-variant text-sm">No one is speaking</span>
              )}
            </div>
          )}

          {ctxUsers.length > 0 && (
            <div className="bg-brand-background dark:bg-brand-surface rounded-lg p-4">
              <h3 className="text-sm font-semibold mb-2">Users in channel ({ctxUsers.length})</h3>
              <div className="space-y-2">{ctxUsersElements}</div>
            </div>
          )}

          {/* Screen Shares Display - Click to View */}
          {availableScreenShares.length > 0 && (
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-4 border border-brand-primary/30 dark:border-brand-primary/30">
              <h3 className="text-sm font-semibold mb-3 text-brand-primary dark:text-brand-primary">
                📺 Screen Shares Available
              </h3>
              <div className="space-y-2">{availableScreenSharesElements}</div>
              <p className="text-xs text-brand-primary dark:text-brand-primary mt-2">
                💡 Click to view screen shares to save bandwidth. Remote screen shares won't start
                until clicked.
              </p>
            </div>
          )}

          {/* Voice Settings Panel (only when connected) */}
          {isConnected && (
            <div className="bg-brand-surface dark:bg-brand-surface rounded-lg border border-brand-outline-variant dark:border-brand-outline shadow-sm">
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-brand-background dark:hover:bg-brand-surface transition-colors rounded-t-lg focus-visible:ring-2 focus-visible:ring-blue-500 focus:outline-none"
                aria-expanded={showSettings}
                aria-label="Toggle Voice Settings"
              >
                <div className="flex items-center gap-2">
                  <FaCog className="text-brand-outline dark:text-brand-on-surface-variant" />
                  <span className="font-medium text-sm text-brand-on-background dark:text-brand-on-primary">
                    Voice Settings
                  </span>
                </div>
                <span className="text-brand-on-surface-variant text-sm">
                  {showSettings ? '▼' : '▶'}
                </span>
              </button>

              {/* Live Captions Overlay */}
              {isConnected && captionsEnabled && captions.length > 0 && (
                <div className="absolute bottom-24 left-0 right-0 z-40 flex flex-col items-center pointer-events-none space-y-1 px-4">
                  {captionsElements}
                </div>
              )}

              {showSettings && (
                <div className="px-4 pb-4 space-y-4 border-t border-brand-outline-variant dark:border-brand-outline pt-4">
                  {/* Audio Devices */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-semibold text-brand-on-surface dark:text-brand-on-surface-variant uppercase tracking-wider">
                        🎧 Audio Devices
                      </label>
                      <button
                        onClick={refreshDevices}
                        className="text-xs text-brand-primary hover:text-brand-primary dark:text-brand-primary hover:underline"
                      >
                        🔄 Refresh
                      </button>
                    </div>

                    <div>
                      <label className="text-xs text-brand-on-surface-variant dark:text-brand-on-surface-variant mb-1 block">
                        Microphone
                      </label>
                      <select
                        value={selectedInputId || 'default'}
                        onChange={(e) =>
                          setInputDevice(e.target.value === 'default' ? null : e.target.value)
                        }
                        className="w-full text-sm px-3 py-2 border border-brand-outline-variant dark:border-brand-outline-variant rounded-lg bg-brand-surface dark:bg-brand-surface-variant text-brand-on-background dark:text-brand-on-primary focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="default">System Default</option>
                        {inputDevicesElements}
                      </select>
                    </div>

                    <div>
                      <label className="text-xs text-brand-on-surface-variant dark:text-brand-on-surface-variant mb-1 block">
                        Speakers
                      </label>
                      <select
                        value={selectedOutputId || 'default'}
                        onChange={(e) =>
                          setOutputDevice(e.target.value === 'default' ? null : e.target.value)
                        }
                        className="w-full text-sm px-3 py-2 border border-brand-outline-variant dark:border-brand-outline-variant rounded-lg bg-brand-surface dark:bg-brand-surface-variant text-brand-on-background dark:text-brand-on-primary focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="default">System Default</option>
                        {outputDevicesElements}
                      </select>
                    </div>
                  </div>

                  {/* Audio Processing */}
                  <div className="space-y-2 pt-2 border-t border-brand-outline-variant dark:border-brand-outline">
                    <label className="text-xs font-semibold text-brand-on-surface dark:text-brand-on-surface-variant uppercase tracking-wider">
                      ✨ Audio Processing
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={voicePreferences.voiceNoiseSuppressionEnabled ?? true}
                        onChange={(e) =>
                          updateVoicePreferences({ voiceNoiseSuppressionEnabled: e.target.checked })
                        }
                        className="w-4 h-4 rounded border-brand-outline-variant text-brand-secondary focus:ring-brand-secondary"
                      />
                      <span className="text-sm text-brand-on-background dark:text-brand-on-primary group-hover:text-brand-secondary dark:group-hover:text-brand-secondary">
                        🔇 Noise Suppression
                      </span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={voicePreferences.voiceEchoCancellationEnabled ?? true}
                        onChange={(e) =>
                          updateVoicePreferences({ voiceEchoCancellationEnabled: e.target.checked })
                        }
                        className="w-4 h-4 rounded border-brand-outline-variant text-brand-secondary focus:ring-brand-secondary"
                      />
                      <span className="text-sm text-brand-on-background dark:text-brand-on-primary group-hover:text-brand-secondary dark:group-hover:text-brand-secondary">
                        🔊 Echo Cancellation
                      </span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={voicePreferences.voiceAutoGainControlEnabled ?? true}
                        onChange={(e) =>
                          updateVoicePreferences({ voiceAutoGainControlEnabled: e.target.checked })
                        }
                        className="w-4 h-4 rounded border-brand-outline-variant text-brand-secondary focus:ring-brand-secondary"
                      />
                      <span className="text-sm text-brand-on-background dark:text-brand-on-primary group-hover:text-brand-secondary dark:group-hover:text-brand-secondary">
                        📊 Auto Gain Control
                      </span>
                    </label>
                  </div>

                  {/* Voice Activation */}
                  <div className="space-y-2 pt-2 border-t border-brand-outline-variant dark:border-brand-outline">
                    <label className="text-xs font-semibold text-brand-on-surface dark:text-brand-on-surface-variant uppercase tracking-wider">
                      🎤 Voice Activation
                    </label>

                    <select
                      value={voicePreferences.voiceActivationMode || 'VOICE_ACTIVITY'}
                      onChange={(e) =>
                        updateVoicePreferences({
                          voiceActivationMode: e.target.value as
                            | 'VOICE_ACTIVITY'
                            | 'PUSH_TO_TALK'
                            | 'ALWAYS_ON',
                        })
                      }
                      className="w-full text-sm px-3 py-2 border border-brand-outline-variant dark:border-brand-outline-variant rounded-lg bg-brand-surface dark:bg-brand-surface-variant text-brand-on-background dark:text-brand-on-primary focus:ring-2 focus:ring-green-500"
                    >
                      <option value="VOICE_ACTIVITY">🎙️ Voice Activity</option>
                      <option value="PUSH_TO_TALK">🔘 Push to Talk</option>
                      <option value="ALWAYS_ON">📡 Always On</option>
                    </select>

                    {(voicePreferences.voiceActivationMode || 'VOICE_ACTIVITY') ===
                      'VOICE_ACTIVITY' && (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="text-xs text-brand-on-surface-variant dark:text-brand-on-surface-variant">
                            Sensitivity
                          </label>
                          <span className="text-xs text-brand-outline dark:text-brand-on-surface-variant">
                            {voicePreferences.voiceActivationThreshold ?? -50} dB
                          </span>
                        </div>
                        <input
                          type="range"
                          min="-100"
                          max="0"
                          step="5"
                          value={voicePreferences.voiceActivationThreshold ?? -50}
                          onChange={(e) =>
                            updateVoicePreferences({
                              voiceActivationThreshold: parseInt(e.target.value),
                            })
                          }
                          className="w-full h-2 bg-brand-surface dark:bg-brand-surface-variant rounded-lg appearance-none cursor-pointer accent-green-600"
                        />
                        <div className="flex justify-between text-xs text-brand-outline mt-1">
                          <span>Low</span>
                          <span>High</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="bg-brand-primary/10 dark:bg-brand-primary/20 border border-brand-primary/30 dark:border-brand-primary/30 rounded-lg p-2">
                    <p className="text-xs text-brand-on-surface-variant dark:text-brand-on-surface-variant">
                      💡 <strong>Note:</strong> Changing devices may require rejoining the channel.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Screen Sharing Settings Panel (only when connected) */}
          {isConnected && (
            <div className="bg-brand-surface dark:bg-brand-surface rounded-lg border border-brand-outline-variant dark:border-brand-outline shadow-sm">
              <button
                onClick={() => setShowScreenSettings(!showScreenSettings)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-brand-background dark:hover:bg-brand-surface transition-colors rounded-t-lg focus-visible:ring-2 focus-visible:ring-blue-500 focus:outline-none"
                aria-expanded={showScreenSettings}
                aria-label="Toggle Screen Sharing Settings"
              >
                <div className="flex items-center gap-2">
                  <FaDesktop className="text-brand-outline dark:text-brand-on-surface-variant" />
                  <span className="font-medium text-sm text-brand-on-background dark:text-brand-on-primary">
                    Screen Sharing Settings
                  </span>
                  {isScreenSharing && (
                    <span className="px-2 py-0.5 text-xs bg-green-500 text-brand-on-primary rounded-full">
                      Active
                    </span>
                  )}
                </div>
                <span className="text-brand-on-surface-variant text-sm">
                  {showScreenSettings ? '▼' : '▶'}
                </span>
              </button>

              {showScreenSettings && (
                <div className="px-4 pb-4 space-y-4 border-t border-brand-outline-variant dark:border-brand-outline pt-4">
                  {/* Source Type Setting */}
                  <div>
                    <label className="text-xs font-semibold text-brand-on-surface dark:text-brand-on-surface-variant uppercase tracking-wider block mb-2">
                      📹 Share Type
                    </label>
                    <select
                      value={screenSourceType}
                      onChange={(e) => setScreenSourceType(e.target.value as 'screen' | 'window')}
                      disabled={isScreenSharing}
                      className="w-full text-sm px-3 py-2 border border-brand-outline-variant dark:border-brand-outline-variant rounded-lg bg-brand-surface dark:bg-brand-surface-variant text-brand-on-background dark:text-brand-on-primary focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="screen">🖥️ Full Screen</option>
                      <option value="window">🪟 Window</option>
                    </select>
                    <p className="text-xs text-brand-outline dark:text-brand-on-surface-variant mt-1">
                      {screenSourceType === 'screen'
                        ? 'Share your entire monitor'
                        : 'Share a specific window or application'}
                    </p>
                  </div>

                  {/* Resolution Setting */}
                  <div>
                    <label className="text-xs font-semibold text-brand-on-surface dark:text-brand-on-surface-variant uppercase tracking-wider block mb-2">
                      📺 Resolution
                    </label>
                    <select
                      value={screenResolution}
                      onChange={(e) =>
                        setScreenResolution(e.target.value as '720p' | '1080p' | 'auto')
                      }
                      disabled={isScreenSharing}
                      className="w-full text-sm px-3 py-2 border border-brand-outline-variant dark:border-brand-outline-variant rounded-lg bg-brand-surface dark:bg-brand-surface-variant text-brand-on-background dark:text-brand-on-primary focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="auto">🤖 Auto (Adaptive)</option>
                      <option value="720p">📱 720p (1280x720)</option>
                      <option value="1080p">🖥️ 1080p (1920x1080)</option>
                    </select>
                  </div>

                  {/* Framerate Setting */}
                  <div>
                    <label className="text-xs font-semibold text-brand-on-surface dark:text-brand-on-surface-variant uppercase tracking-wider block mb-2">
                      🎬 Framerate
                    </label>
                    <select
                      value={screenFramerate}
                      onChange={(e) => setScreenFramerate(parseInt(e.target.value) as 15 | 30 | 60)}
                      disabled={isScreenSharing}
                      className="w-full text-sm px-3 py-2 border border-brand-outline-variant dark:border-brand-outline-variant rounded-lg bg-brand-surface dark:bg-brand-surface-variant text-brand-on-background dark:text-brand-on-primary focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="15">🐢 15 FPS (Low bandwidth)</option>
                      <option value="30">⚡ 30 FPS (Balanced)</option>
                      <option value="60">🚀 60 FPS (Smooth)</option>
                    </select>
                  </div>

                  {/* Screen Share Mute */}
                  {isScreenSharing && (
                    <button
                      onClick={toggleScreenMute}
                      className={`w-full py-2 px-3 rounded-lg font-medium text-sm transition-colors ${
                        isScreenMuted
                          ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50'
                          : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50'
                      }`}
                    >
                      {isScreenMuted ? '🔇 Muted - Click to Resume' : '🔊 Active - Click to Mute'}
                    </button>
                  )}

                  {/* Connection Stats */}
                  {isScreenSharing && (
                    <div className="bg-brand-background dark:bg-brand-background/50 rounded-lg p-3 space-y-2">
                      <div className="text-xs font-semibold text-brand-on-surface dark:text-brand-on-surface-variant uppercase tracking-wider">
                        📊 Connection Stats
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-brand-on-surface-variant dark:text-brand-on-surface-variant">
                            Quality:
                          </span>
                          <span
                            className={`ml-2 font-medium ${
                              connectionQuality === 'good'
                                ? 'text-green-500 dark:text-green-400'
                                : connectionQuality === 'medium'
                                  ? 'text-yellow-500 dark:text-yellow-400'
                                  : 'text-brand-error dark:text-brand-error'
                            }`}
                          >
                            {connectionQuality === 'good'
                              ? '✅ Good'
                              : connectionQuality === 'medium'
                                ? '⚠️ Medium'
                                : '❌ Poor'}
                          </span>
                        </div>
                        <div>
                          <span className="text-brand-on-surface-variant dark:text-brand-on-surface-variant">
                            Latency:
                          </span>
                          <span className="ml-2 font-medium text-brand-on-background dark:text-brand-on-primary">
                            {latency.toFixed(0)}ms
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="bg-brand-primary/10 dark:bg-brand-primary/20 border border-brand-primary/30 dark:border-brand-primary/30 rounded-lg p-2">
                    <p className="text-xs text-brand-on-surface-variant dark:text-brand-on-surface-variant">
                      💡 <strong>Tip:</strong> Use "Auto" resolution for adaptive quality based on
                      your connection. Settings can only be changed before starting screen share.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-3 justify-center p-2 rounded-full bg-brand-surface/95 dark:bg-[#19163e]/95 backdrop-blur-xl border border-brand-outline-variant/60 shadow-2xl">
            {isConnected ? (
              <>
                <button
                  onClick={toggleMute}
                  className={`w-12 h-12 rounded-full flex items-center justify-center shadow-md active:scale-95 transition-all cursor-pointer focus-visible:ring-2 focus-visible:ring-primary-500 focus:outline-none ${
                    isMuted
                      ? 'bg-red-500 text-white hover:bg-red-600 shadow-red-500/20'
                      : 'bg-brand-surface-variant text-brand-on-surface hover:bg-white/15'
                  }`}
                  aria-label={isMuted ? 'Unmute microphone' : 'Mute microphone'}
                  aria-pressed={isMuted}
                  title={isMuted ? 'Unmute' : 'Mute'}
                >
                  {isMuted ? <FaMicrophoneSlash size={18} /> : <FaMicrophone size={18} />}
                </button>
                <button
                  onClick={toggleDeafen}
                  className={`w-12 h-12 rounded-full flex items-center justify-center shadow-md active:scale-95 transition-all cursor-pointer focus-visible:ring-2 focus-visible:ring-primary-500 focus:outline-none ${
                    isDeafened
                      ? 'bg-red-500 text-white hover:bg-red-600 shadow-red-500/20'
                      : 'bg-brand-surface-variant text-brand-on-surface hover:bg-white/15'
                  }`}
                  aria-label={isDeafened ? 'Undeafen audio' : 'Deafen audio'}
                  aria-pressed={isDeafened}
                  title={isDeafened ? 'Undeafen' : 'Deafen'}
                >
                  <FaHeadphones size={18} />
                </button>
                <button
                  onClick={toggleCaptions}
                  className={`w-12 h-12 rounded-full flex items-center justify-center shadow-md active:scale-95 transition-all cursor-pointer focus-visible:ring-2 focus-visible:ring-primary-500 focus:outline-none ${
                    captionsEnabled
                      ? 'bg-primary-600 text-white hover:bg-primary-500 shadow-primary-600/20'
                      : 'bg-brand-surface-variant text-brand-on-surface hover:bg-white/15'
                  }`}
                  title={captionsEnabled ? 'Disable Live Captions' : 'Enable Live Captions'}
                >
                  <FaClosedCaptioning size={18} />
                </button>
                <button
                  onClick={isScreenSharing ? stopScreenShare : startScreenShare}
                  className={`w-12 h-12 rounded-full flex items-center justify-center shadow-md active:scale-95 transition-all cursor-pointer focus-visible:ring-2 focus-visible:ring-primary-500 focus:outline-none ${
                    isScreenSharing
                      ? 'bg-primary-600 text-white hover:bg-primary-500 shadow-primary-600/20'
                      : 'bg-brand-surface-variant text-brand-on-surface hover:bg-white/15'
                  }`}
                  title={isScreenSharing ? 'Stop Screen Share' : 'Start Screen Share'}
                  aria-label={isScreenSharing ? 'Stop Screen Share' : 'Start Screen Share'}
                  aria-pressed={isScreenSharing}
                >
                  <FaDesktop size={18} />
                </button>
                <button
                  onClick={leaveVoiceChannel}
                  className="w-12 h-12 rounded-full bg-red-600 hover:bg-red-500 text-white flex items-center justify-center shadow-lg hover:shadow-red-600/30 active:scale-95 transition-all cursor-pointer focus-visible:ring-2 focus-visible:ring-white focus:outline-none"
                  aria-label="Leave voice channel"
                  title="Leave"
                >
                  <FaPhone size={18} className="rotate-[135deg]" />
                </button>
              </>
            ) : (
              <button
                onClick={() => {
                  setHasClicked(true);
                  joinVoiceChannel();
                }}
                className="btn btn-primary px-8 py-3.5 text-base font-semibold rounded-full shadow-lg hover:shadow-xl active:scale-95 transition-all cursor-pointer"
              >
                🔊 Join Voice Channel
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
